import { derived, get, writable } from 'svelte/store';
import { browser } from '$app/environment';
import { region } from '$lib/region';
import { page } from '$app/stores';
import type {
  CompleteCartFragment,
  GetCartQuery,
  GetProductQuery,
  ProductCardFragment,
  QuickAddToCartFragment
} from '$gql/storefront/operations';
import type {
  CategorySaleStoryblok,
  FreeGiftSaleStoryblok,
  ProductDetailPageStoryblok,
  ProgressBarConfigStoryblok,
  TieredSaleStoryblok
} from '$types/bloks';
import { getCurrentPath } from '$lib/utils/paths';
import { cart } from '$lib/cart';
import { asyncDerived, type Readable } from '@square/svelte-store';
import { shopify } from '$lib/shopify';
import type { StoryblokStory } from 'storyblok-generate-ts';
import { isCmsStory } from '$lib/utils/cms';
import { currentWishlists } from '$lib/wishlist';
import { getProductCategory } from '$lib/cart/cart';
import {
  BLACK_CAMPAIGN_COLOR,
  CAMPAIGN_COLORS,
  DEFAULT_CAMPAIGN_COLOR,
  FREE_GIFT
} from '$lib/constants';

export const tieredCampaignPdpExclude = writable(false);
export const categoryCampaignPdpExclude = writable(false);

export const tieredCampaign = derived(
  [page, region, tieredCampaignPdpExclude],
  ([$page, $region, $tieredCampaignPdpExclude]) => {
    // Find 1st campaign that matches current region
    const active = $page.data.configuration.content.campaigns?.find(
      (c) => c.component === 'tiered-sale' && c.regions.includes($region)
    ) as TieredSaleStoryblok;

    if (!browser || !active) {
      return;
    }

    const {
      tag_filter,
      tag_filter_type,
      discount,
      campaign_color,
      promotional_blocks,
      show_promotional_banners_on_pdp,
      show_promotional_banners_on_shop_pages,
      path_filter,
      path_filter_type
    } = active;

    const tagFilters = (tag_filter || '').split('\n');
    const blacklist = (path_filter || '').split('\n');
    const validatePath = (pathname: string): boolean => {
      const filter = path_filter_type || 'exclude';
      if (filter === 'exclude') return blacklist.includes(pathname);
      return !blacklist.includes(pathname);
    };

    const hideSaleComponents =
      validatePath('/' + getCurrentPath($page.params)) || $tieredCampaignPdpExclude;

    const filterProducts = (
      product:
        | ProductCardFragment
        | NonNullable<GetProductQuery['product']>
        | QuickAddToCartFragment
    ) => {
      const filter = tag_filter_type || 'exclude';
      if (filter === 'exclude') {
        return tagFilters.some((exc) => product.tags.includes(exc));
      }
      return tagFilters.some((exc) => !product.tags.includes(exc));
    };

    return {
      isProductExcluded: (
        product:
          | ProductCardFragment
          | NonNullable<GetProductQuery['product']>
          | QuickAddToCartFragment
          | NonNullable<GetCartQuery['cart']>['lines']['nodes'][number]['merchandise']['product']
      ) => filterProducts(product),
      discount,
      tag_filter_type,
      tagFilters,
      getDiscountedPrice: (price: number) => Math.ceil(price * (1 - Number(discount) / 100)),
      campaign_color,
      promotional_blocks,
      showBannersOnPDP: !!show_promotional_banners_on_pdp,
      showBannersOnShopPages: !!show_promotional_banners_on_shop_pages,
      path_filter,
      path_filter_type,
      hideSaleComponents
    };
  }
);

export const progressBarConfig = derived([page, region], ([$page, $region]) => {
  // Find 1st campaign that matches current region
  const active = $page.data.configuration.content.bars?.find(
    (c: ProgressBarConfigStoryblok) =>
      c.component === 'progress-bar-config' && c.regions.includes($region)
  ) as ProgressBarConfigStoryblok;

  if (!browser || !active) {
    return;
  }

  const {
    tag_filter,
    tag_filter_type,
    campaign_color,
    tiers,
    amount_to_start,
    path_filter,
    path_filter_type,
    countdown_type,
    ends_in
  } = active;

  const tagFilters = (tag_filter || '').split('\n');
  const blacklist = (path_filter || '').split('\n');
  const validatePath = (pathname: string): boolean => {
    const filter = path_filter_type || 'exclude';
    if (filter === 'exclude') return blacklist.includes(pathname);
    return !blacklist.includes(pathname);
  };

  const hideSaleComponents = validatePath('/' + getCurrentPath($page.params));

  const filterProducts = (
    product: ProductCardFragment | NonNullable<GetProductQuery['product']> | QuickAddToCartFragment
  ) => {
    const filter = tag_filter_type || 'exclude';
    if (filter === 'exclude') {
      return tagFilters.some((exc) => product.tags.includes(exc));
    }
    return tagFilters.some((exc) => !product.tags.includes(exc));
  };

  return {
    isProductExcluded: (
      product:
        | ProductCardFragment
        | NonNullable<GetProductQuery['product']>
        | QuickAddToCartFragment
        | NonNullable<GetCartQuery['cart']>['lines']['nodes'][number]['merchandise']['product']
    ) => filterProducts(product),
    tag_filter_type,
    tagFilters,
    campaign_color,
    tiers,
    tierOffset: +amount_to_start || 1,
    path_filter,
    path_filter_type,
    hideSaleComponents,
    countdown_type,
    ends_in
  };
});

export const categoryCampaigns = derived(
  [page, region, categoryCampaignPdpExclude],
  ([$page, $region, $categoryCampaignPdpExclude]) => {
    // Find 1st campaign that matches current region
    const activeSales = $page.data.configuration.content.campaigns?.filter(
      (c) => c.component === 'category-sale' && c.regions.includes($region)
    ) as CategorySaleStoryblok[];

    if (!browser || !activeSales.length) {
      return;
    }

    const arr = activeSales.map((active) => {
      const {
        tag_filter,
        tag_filter_type,
        discount,
        promotional_blocks,
        show_promotional_banners_on_pdp,
        show_promotional_banners_on_shop_pages,
        path_filter,
        path_filter_type,
        color,
        campaign_color
      } = active;

      const tagFilters = (tag_filter || '').split('\n');
      const blacklist = (path_filter || '').split('\n');
      const validatePath = (pathname: string): boolean => {
        const filter = path_filter_type || 'exclude';
        if (filter === 'exclude') return blacklist.includes(pathname);
        return !blacklist.includes(pathname);
      };

      const hideSaleComponents =
        validatePath('/' + getCurrentPath($page.params)) || $categoryCampaignPdpExclude;

      const filterProducts = (
        product:
          | ProductCardFragment
          | NonNullable<GetProductQuery['product']>
          | QuickAddToCartFragment
      ) => {
        const filter = tag_filter_type || 'exclude';
        if (filter === 'exclude') {
          return tagFilters.some((exc) => product.tags.includes(exc));
        }
        return tagFilters.some((exc) => !product.tags.includes(exc));
      };

      return {
        isProductExcluded: (
          product:
            | ProductCardFragment
            | NonNullable<GetProductQuery['product']>
            | QuickAddToCartFragment
        ) => filterProducts(product),
        tag_filter_type,
        tagFilters,
        discount,
        getDiscountedPrice: (price: number) => Math.ceil(price * (1 - Number(discount) / 100)),
        promotional_blocks,
        showBannersOnPDP: !!show_promotional_banners_on_pdp,
        showBannersOnShopPages: !!show_promotional_banners_on_shop_pages,
        path_filter,
        path_filter_type,
        hideSaleComponents,
        color,
        campaign_color
      };
    });

    return arr;
  }
);

export const freeGiftCampaign = derived([page, region], ([$page, $region]) => {
  // Find 1st campaign that matches current region
  const active = $page.data.configuration.content.campaigns?.find(
    (c) => c.component === 'free-gift-sale' && c.regions.includes($region)
  ) as FreeGiftSaleStoryblok;

  if (!browser || !active) {
    return;
  }

  const {
    campaign_free_gifts,
    show_free_gift_on_tier_level,
    tag_filter,
    tag_filter_type,
    free_gift_cart_position,
    free_gift_cart_outline,
    campaign_color,
    promotional_blocks,
    free_gift_pdp_button_text,
    free_gift_pdp_button_link,
    cart_banner_title_label,
    cart_banner_select_gift_button_label,
    cart_banner_image,
    add_gift_automatically,
    free_gifts_cannot_be_bought
  } = active;

  const tagFilters = (tag_filter || '').split('\n');

  const filterProducts = (
    product: ProductCardFragment | NonNullable<GetProductQuery['product']> | QuickAddToCartFragment
  ) => {
    const filter = tag_filter_type || 'exclude';
    if (filter === 'exclude') {
      return tagFilters.some((exc) => product.tags.includes(exc));
    }
    return tagFilters.some((exc) => !product.tags.includes(exc));
  };

  return {
    isProductExcluded: (
      product:
        | ProductCardFragment
        | NonNullable<GetProductQuery['product']>
        | QuickAddToCartFragment
        | NonNullable<GetCartQuery['cart']>['lines']['nodes'][number]['merchandise']['product']
    ) => filterProducts(product),
    campaign_free_gifts,
    free_gifts_cannot_be_bought,
    show_free_gift_on_tier_level,
    tag_filter_type,
    tagFilters,
    free_gift_cart_position,
    free_gift_cart_outline,
    campaign_color,
    promotional_blocks,
    free_gift_pdp_button_text,
    free_gift_pdp_button_link,
    cart_banner_title_label,
    cart_banner_select_gift_button_label,
    cart_banner_image,
    add_gift_automatically
  };
});

export const cartFilteredByTierForFreeGift: Readable<CompleteCartFragment | null | undefined> =
  derived([cart, freeGiftCampaign], ([$cart, $freeGiftCampaign]) => {
    const lines = $cart?.lines.nodes.filter((line) => {
      const filter = $freeGiftCampaign?.tag_filter_type || 'exclude';
      if (
        filter === 'exclude' &&
        $freeGiftCampaign?.tagFilters.some((exclusion) =>
          line.merchandise.product.tags.includes(exclusion)
        )
      ) {
        return false;
      }
      if (
        filter === 'include' &&
        $freeGiftCampaign?.tagFilters.some(
          (exclusion) => !line.merchandise.product.tags.includes(exclusion)
        )
      ) {
        return false;
      }

      if (
        line.attributes?.some((item) => item.key == FREE_GIFT) ||
        line.cost.totalAmount.amount === '0.0'
      ) {
        return false;
      }

      return true;
    });

    return {
      ...$cart,
      lines: {
        ...$cart?.lines,
        nodes: lines || []
      }
    } as CompleteCartFragment;
  });

const getMostFrequentProductType = (cart: CompleteCartFragment | null | undefined): string[] => {
  const productTypeQuantities: Record<string, number> = {};
  let maxQuantity = 0;
  let mostFrequentProductTypes: string[] = [];

  if (!cart) return [];

  // Iterate over each product in the cart
  cart.lines.nodes.forEach((line) => {
    const { productType } = line.merchandise.product;

    productTypeQuantities[productType] = (productTypeQuantities[productType] || 0) + line.quantity;

    if (productTypeQuantities[productType] > maxQuantity) {
      maxQuantity = productTypeQuantities[productType];
      mostFrequentProductTypes = [productType];
    } else if (productTypeQuantities[productType] === maxQuantity) {
      mostFrequentProductTypes.push(productType);
    }
  });

  return mostFrequentProductTypes;
};

interface FreeGiftType extends StoryblokStory<ProductDetailPageStoryblok> {
  productType: string | null;
}

export const getFreeProductsSlugsOrderByPriority = asyncDerived(
  [cart, currentWishlists, freeGiftCampaign, cartFilteredByTierForFreeGift],
  async ([$cart, $currentWishlists, $freeGiftCampaign, $cartFilteredByTierForFreeGift]) => {
    try {
      const wishlists = ($currentWishlists || []).nodes.filter(
        (product) => product?.__typename === 'Product'
      );
      const freeGifts = (($freeGiftCampaign?.campaign_free_gifts as FreeGiftType[]) || []).map(
        (gift) => {
          const freeGift = gift as FreeGiftType;
          return {
            ...freeGift,
            productType: getProductCategory(freeGift.name)
          };
        }
      );

      const cartSlugs = $cart?.lines.nodes.map((line) => line.merchandise.product.handle) || [];
      const availableGifts = freeGifts.filter((gift) => !cartSlugs.includes(gift?.slug));
      const wishlistFreeGifts = wishlists.filter((product) =>
        availableGifts.some((gift) => product.handle === gift?.slug)
      );
      const mostFrequentCategory: string[] = getMostFrequentProductType(
        $cartFilteredByTierForFreeGift
      );

      const availableGiftsSorted = availableGifts.sort((item1, item2) => {
        const item1InWishlist = wishlistFreeGifts.some((gift) => gift.handle == item1.slug);
        const item2InWishlist = wishlistFreeGifts.some((gift) => gift.handle == item2.slug);

        // Item 1 is in wishlist and item 2 isn't
        if (item1InWishlist && !item2InWishlist) {
          return -1; // Item 1 should come first

          // Item 1 isn't in wishlist and item 2 is
        } else if (!item1InWishlist && item2InWishlist) {
          return 1; // Item 1 should come second

          // Both items are in wishlist or aren't in the wishlist
        } else if (item1InWishlist == item2InWishlist) {
          const item1IsOneOfMostFrequentCategory = mostFrequentCategory.some(
            (c) => item1.productType?.toLowerCase() === c.toLowerCase()
          );
          const item2IsOneOfMostFrequentCategory = mostFrequentCategory.some(
            (c) => item2.productType?.toLowerCase() === c.toLowerCase()
          );

          // Item 1 has one of the most frequent categories and item 2 hasn't
          if (item1IsOneOfMostFrequentCategory && !item2IsOneOfMostFrequentCategory) {
            return -1; // Item 1 should come first

            // Item 1 hasnt one of the most frequent categories and item 2 has
          } else if (!item1IsOneOfMostFrequentCategory && item2IsOneOfMostFrequentCategory) {
            return 1; // Item 1 should come second

            // Neither item 1 or item 2 have one of the most frequent categories
          } else if (!item1IsOneOfMostFrequentCategory && !item2IsOneOfMostFrequentCategory) {
            return 0; // It doesnt matter if item 1 or item 2 come first

            // Both item 1 and item 2 have one of the most frequent categories
          } else if (item1IsOneOfMostFrequentCategory && item2IsOneOfMostFrequentCategory) {
            const item1IsARing = item1.productType?.toLowerCase() === 'rings';
            const item2IsARing = item2.productType?.toLowerCase() === 'rings';

            // Item 1 is a ring and item 2 is not
            if (item1IsARing && !item2IsARing) {
              return 1; // Item 1 should come second

              // Item 1 is not a ring and item 2 is
            } else if (!item1IsARing && item2IsARing) {
              return -1; // Item 1 should come first

              // Item 1 and item 2 are both rings or not
            } else if (item1IsARing == item2IsARing) {
              return 0; // It doesnt matter if item 1 or item 2 come first
            }
          }
        }

        return 0; // It doesnt matter if item 1 or item 2 come first
      });

      return availableGiftsSorted.map((freeGift) => freeGift.slug);
    } catch (error) {
      return null;
    }
  },
  {
    trackState: true
  }
);

export const campaignProgressLevelProduct = asyncDerived(
  [
    cart,
    shopify,
    freeGiftCampaign,
    cartFilteredByTierForFreeGift,
    getFreeProductsSlugsOrderByPriority
  ],
  async ([
    $cart,
    $shopify,
    $freeGiftCampaign,
    $cartFilteredByTierForFreeGift,
    $getFreeProductsSlugsOrderByPriority
  ]) => {
    const freeGiftLevel = Number($freeGiftCampaign?.show_free_gift_on_tier_level || 0);

    const currentLevel =
      $cartFilteredByTierForFreeGift?.lines.nodes.reduce((acc, line) => {
        return acc + line.quantity;
      }, 0) || 0;

    const alreadyHasFreeGift = $cart?.lines.nodes.some((l) =>
      l.attributes?.some((item) => item.key == FREE_GIFT)
    );

    // check if the free gift level was set and the current level is at the free gift level
    if (
      !freeGiftLevel ||
      currentLevel !== freeGiftLevel ||
      alreadyHasFreeGift ||
      !$freeGiftCampaign?.add_gift_automatically
    ) {
      return null;
    }

    const freeGifts =
      ($freeGiftCampaign?.campaign_free_gifts as StoryblokStory<ProductDetailPageStoryblok>[]) ||
      [];

    if (!freeGifts || !freeGifts?.length || !isCmsStory(freeGifts[0])) {
      return null;
    }

    const freeProductsOrderByPriority = $getFreeProductsSlugsOrderByPriority;

    if (freeProductsOrderByPriority) {
      for (const freeGiftSlug of freeProductsOrderByPriority) {
        const getProduct = await $shopify.getProduct({ handle: freeGiftSlug });

        if (getProduct.product?.availableForSale) {
          return getProduct.product;
        }
      }
    }
  }
);

export interface ColorObject {
  color_label: string;
  color_value: string;
  name: string;
  value: string;
}

export interface CampaignColors {
  campaignColors: Record<string, ColorObject[]>;
  campaignColorKeys: (string | number)[];
  activeColor: string | number;
  isBlackColor: boolean;
  selectedColors: ColorObject[];
  backgroundColor?: string;
  textColor?: string;
  altTextColor?: string;
  altBackgroundColor?: string;
}

export const tieredCampaignColors = derived([page, tieredCampaign], ([$page, $tieredCampaign]) => {
  const campaignColors: Record<string, ColorObject[]> = $page?.data?.campaignColors;
  const campaignColorKeys: (string | number)[] = $page?.data?.campaignColorKeys;

  const activeTieredColor = campaignColorKeys.includes(
    $tieredCampaign?.campaign_color as string | number
  )
    ? $tieredCampaign?.campaign_color
    : DEFAULT_CAMPAIGN_COLOR;

  const isBlackTieredColor = activeTieredColor === BLACK_CAMPAIGN_COLOR;
  const selectedTieiredColors = campaignColors[activeTieredColor as string];

  let tieredAltTextColor: string | undefined = undefined;
  let tieredTextColor: string | undefined = undefined;
  let tieredAltBackgroundColor: string | undefined = undefined;
  let tieredBackgroundColor: string | undefined = undefined;

  if (selectedTieiredColors) {
    tieredBackgroundColor = selectedTieiredColors.find(
      (item: { color_label: string }) => item?.color_label === CAMPAIGN_COLORS['tone-1']
    )?.value;
    tieredTextColor = selectedTieiredColors.find(
      (item: { color_label: string }) => item?.color_label === CAMPAIGN_COLORS['tone-2']
    )?.value;
    tieredAltTextColor = selectedTieiredColors.find(
      (item: { color_label: string }) => item?.color_label === CAMPAIGN_COLORS['tone-3']
    )?.value;
    tieredAltBackgroundColor = selectedTieiredColors.find(
      (item: { color_label: string }) => item?.color_label === CAMPAIGN_COLORS['tone-4']
    )?.value;
  }

  return {
    campaignColors,
    campaignColorKeys,
    activeTieredColor,
    isBlackTieredColor,
    selectedTieiredColors,
    tieredBackgroundColor,
    tieredTextColor,
    tieredAltTextColor,
    tieredAltBackgroundColor
  };
});

export const getColorData = (): {
  campaignColors: Record<string, ColorObject[]>;
  campaignColorKeys: (string | number)[];
} => {
  const data = get(page);

  return {
    campaignColors: data?.data?.campaignColors as Record<string, ColorObject[]>,
    campaignColorKeys: data?.data?.campaignColorKeys as (string | number)[]
  };
};

export const getCampaignColors = (campaign_color: string | number): CampaignColors => {
  const { campaignColors, campaignColorKeys } = getColorData();

  const activeColor = campaignColorKeys.includes(campaign_color)
    ? campaign_color
    : DEFAULT_CAMPAIGN_COLOR;

  const isBlackColor = activeColor === BLACK_CAMPAIGN_COLOR;
  const selectedColors = campaignColors[activeColor as string];

  let backgroundColor: string | undefined = undefined;
  let textColor: string | undefined = undefined;
  let altTextColor: string | undefined = undefined;
  let altBackgroundColor: string | undefined = undefined;

  if (selectedColors) {
    backgroundColor = selectedColors.find(
      (item: { color_label: string }) => item?.color_label === CAMPAIGN_COLORS['tone-1']
    )?.value;
    textColor = selectedColors.find(
      (item: { color_label: string }) => item?.color_label === CAMPAIGN_COLORS['tone-2']
    )?.value;
    altTextColor = selectedColors.find(
      (item: { color_label: string }) => item?.color_label === CAMPAIGN_COLORS['tone-3']
    )?.value;
    altBackgroundColor = selectedColors.find(
      (item: { color_label: string }) => item?.color_label === CAMPAIGN_COLORS['tone-4']
    )?.value;
  }

  return {
    campaignColors,
    campaignColorKeys,
    activeColor,
    isBlackColor,
    selectedColors,
    backgroundColor,
    textColor,
    altTextColor,
    altBackgroundColor
  };
};
